import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  LinearProgress,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Analytics,
  TrendingUp,
  Speed,
  Psychology,
  Insights,
  Schedule,
  Business,
  CheckCircle,
  Error,
  Star
} from '@mui/icons-material';
import { InterviewSummary } from '../types';

interface UserJourney {
  id: string;
  timestamp: Date;
  action: 'query' | 'interview' | 'feedback' | 'bookmark';
  details: string;
  success: boolean;
  duration?: number;
  category: string;
}

interface BusinessImpact {
  metric: string;
  value: number;
  change: number;
  trend: 'up' | 'down' | 'stable';
  category: 'efficiency' | 'accuracy' | 'engagement';
}

interface OptimizationSuggestion {
  id: string;
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  effort: 'low' | 'medium' | 'high';
  category: string;
  priority: number;
}

interface AdvancedAnalyticsProps {
  currentKnowledge?: InterviewSummary | null;
  recentQueries: string[];
  onOptimize?: (suggestion: OptimizationSuggestion) => void;
}

export function AdvancedAnalytics({ 
  currentKnowledge, 
  recentQueries, 
  onOptimize 
}: AdvancedAnalyticsProps) {
  const [timeframe, setTimeframe] = useState<'day' | 'week' | 'month'>('week');
  const [selectedMetric, setSelectedMetric] = useState<string>('overview');
  const [userJourney, setUserJourney] = useState<UserJourney[]>([]);
  const [businessImpacts, setBusinessImpacts] = useState<BusinessImpact[]>([]);
  const [optimizationSuggestions, setOptimizationSuggestions] = useState<OptimizationSuggestion[]>([]);

  // Initialize analytics data
  const generateMockData = React.useCallback(() => {
    // Generate user journey data
    const journey: UserJourney[] = [
      {
        id: '1',
        timestamp: new Date(Date.now() - 3600000),
        action: 'query',
        details: 'Asked about revenue trends',
        success: true,
        duration: 2.3,
        category: 'business_intelligence'
      },
      {
        id: '2',
        timestamp: new Date(Date.now() - 7200000),
        action: 'interview',
        details: 'Completed table relationships interview',
        success: true,
        duration: 12.5,
        category: 'knowledge_building'
      },
      {
        id: '3',
        timestamp: new Date(Date.now() - 10800000),
        action: 'feedback',
        details: 'Provided positive feedback on query response',
        success: true,
        category: 'user_experience'
      }
    ];
    setUserJourney(journey);

    // Generate business impact metrics
    const impacts: BusinessImpact[] = [
      {
        metric: 'Query Success Rate',
        value: 92,
        change: 12,
        trend: 'up',
        category: 'accuracy'
      },
      {
        metric: 'Average Response Time',
        value: 2.1,
        change: -0.5,
        trend: 'up',
        category: 'efficiency'
      },
      {
        metric: 'User Satisfaction',
        value: 4.3,
        change: 0.4,
        trend: 'up',
        category: 'engagement'
      },
      {
        metric: 'Knowledge Coverage',
        value: currentKnowledge ? Math.min(100, (currentKnowledge.tablesDiscovered.length / 8) * 100) : 0,
        change: 15,
        trend: 'up',
        category: 'accuracy'
      }
    ];
    setBusinessImpacts(impacts);

    // Generate optimization suggestions
    const suggestions: OptimizationSuggestion[] = [
      {
        id: 'interview_prompt',
        title: 'Proactive Interview Suggestions',
        description: 'Suggest interviews when knowledge gaps are detected',
        impact: 'high',
        effort: 'low',
        category: 'knowledge_building',
        priority: 1
      },
      {
        id: 'query_caching',
        title: 'Intelligent Query Caching',
        description: 'Cache frequently asked queries for faster responses',
        impact: 'medium',
        effort: 'medium',
        category: 'performance',
        priority: 2
      },
      {
        id: 'context_learning',
        title: 'Enhanced Context Learning',
        description: 'Learn from user corrections and preferences',
        impact: 'high',
        effort: 'high',
        category: 'accuracy',
        priority: 3
      }
    ];
    setOptimizationSuggestions(suggestions);
  }, [currentKnowledge]);

  useEffect(() => {
    generateMockData();
  }, [generateMockData]);

  // Calculate analytics metrics
  const analytics = useMemo(() => {
    const totalQueries = recentQueries.length;
    const successfulQueries = Math.round(totalQueries * 0.92); // 92% success rate
    const averageResponseTime = 2.1;
    const knowledgeUtilization = currentKnowledge ? 
      (currentKnowledge.tablesDiscovered.length + currentKnowledge.relationshipsLearned.length + currentKnowledge.businessRules.length) : 0;

    return {
      totalQueries,
      successfulQueries,
      averageResponseTime,
      knowledgeUtilization,
      userEngagement: Math.min(100, totalQueries * 2), // Simple engagement score
      learningVelocity: currentKnowledge ? Math.round(currentKnowledge.confidenceScore * 100) : 0
    };
  }, [recentQueries, currentKnowledge]);

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getEffortColor = (effort: string) => {
    switch (effort) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'up': return <TrendingUp color="success" />;
      case 'down': return <TrendingUp sx={{ transform: 'rotate(180deg)', color: 'error.main' }} />;
      default: return <TrendingUp sx={{ transform: 'rotate(90deg)', color: 'warning.main' }} />;
    }
  };

  const renderOverview = () => (
    <Grid container spacing={3}>
      {/* Key Metrics */}
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Performance Metrics
            </Typography>
            <Grid container spacing={2}>
              {businessImpacts.map((impact) => (
                <Grid item xs={6} md={3} key={impact.metric}>
                  <Paper sx={{ p: 2, textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                      {getTrendIcon(impact.trend)}
                      <Typography variant="h4" sx={{ ml: 1, fontWeight: 'bold' }}>
                        {impact.metric.includes('Time') ? `${impact.value}s` : 
                         impact.metric.includes('Rate') || impact.metric.includes('Coverage') ? `${impact.value}%` :
                         impact.value}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {impact.metric}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color={impact.trend === 'up' ? 'success.main' : impact.trend === 'down' ? 'error.main' : 'warning.main'}
                    >
                      {impact.change > 0 ? '+' : ''}{impact.change}
                      {impact.metric.includes('Time') ? 's' : impact.metric.includes('Rate') || impact.metric.includes('Coverage') ? '%' : ''}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Grid>

      {/* Quick Stats */}
      <Grid item xs={12} md={4}>
        <Card sx={{ height: '100%' }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Quick Stats
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon><Psychology /></ListItemIcon>
                <ListItemText
                  primary="Total Queries"
                  secondary={analytics.totalQueries}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><CheckCircle /></ListItemIcon>
                <ListItemText
                  primary="Successful Queries"
                  secondary={`${analytics.successfulQueries} (${Math.round((analytics.successfulQueries/analytics.totalQueries)*100)}%)`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Speed /></ListItemIcon>
                <ListItemText
                  primary="Avg Response Time"
                  secondary={`${analytics.averageResponseTime}s`}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><Insights /></ListItemIcon>
                <ListItemText
                  primary="Knowledge Items"
                  secondary={analytics.knowledgeUtilization}
                />
              </ListItem>
            </List>
          </CardContent>
        </Card>
      </Grid>

      {/* User Journey */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Recent User Journey
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Details</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userJourney.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>
                        {item.timestamp.toLocaleTimeString()}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={item.action} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{item.details}</TableCell>
                      <TableCell>
                        {item.duration ? `${item.duration}s` : '-'}
                      </TableCell>
                      <TableCell>
                        {item.success ? 
                          <CheckCircle color="success" fontSize="small" /> : 
                          <Error color="error" fontSize="small" />
                        }
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderOptimizations = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <TrendingUp sx={{ mr: 1 }} />
        Auto-Optimization Suggestions
      </Typography>
      
      <Alert severity="info" sx={{ mb: 2 }}>
        Based on your usage patterns, here are AI-powered optimization recommendations:
      </Alert>

      {optimizationSuggestions.map((suggestion) => (
        <Card key={suggestion.id} sx={{ mb: 2 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  {suggestion.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {suggestion.description}
                </Typography>
              </Box>
              <Button
                variant="contained"
                size="small"
                onClick={() => onOptimize?.(suggestion)}
              >
                Apply
              </Button>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip 
                label={`${suggestion.impact} impact`}
                color={getImpactColor(suggestion.impact) as any}
                size="small"
              />
              <Chip 
                label={`${suggestion.effort} effort`}
                color={getEffortColor(suggestion.effort) as any}
                size="small"
                variant="outlined"
              />
              <Chip 
                label={suggestion.category}
                size="small"
                variant="outlined"
              />
              <Chip 
                label={`Priority ${suggestion.priority}`}
                size="small"
                variant="outlined"
              />
            </Box>
          </CardContent>
        </Card>
      ))}
    </Box>
  );

  const renderInsights = () => (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <Insights sx={{ mr: 1 }} />
        Business Insights
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Usage Patterns
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><Schedule /></ListItemIcon>
                  <ListItemText
                    primary="Peak Usage Time"
                    secondary="2:00 PM - 4:00 PM"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Business /></ListItemIcon>
                  <ListItemText
                    primary="Most Asked Category"
                    secondary="Revenue Analysis"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><Star /></ListItemIcon>
                  <ListItemText
                    primary="Success Rate Trend"
                    secondary="↗️ Improving (+12% this week)"
                  />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Learning Progress
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Knowledge Completion
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={analytics.learningVelocity}
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {analytics.learningVelocity}% complete
                </Typography>
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Query Accuracy
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={92}
                  color="success"
                  sx={{ height: 8, borderRadius: 4 }}
                />
                <Typography variant="caption" color="text.secondary">
                  92% accuracy rate
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  return (
    <Box>
      {/* Controls */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <Analytics sx={{ mr: 1 }} />
              Advanced Analytics
            </Typography>
            
            <ToggleButtonGroup
              value={timeframe}
              exclusive
              onChange={(e, newTimeframe) => newTimeframe && setTimeframe(newTimeframe)}
              size="small"
            >
              <ToggleButton value="day">Day</ToggleButton>
              <ToggleButton value="week">Week</ToggleButton>
              <ToggleButton value="month">Month</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          
          <ToggleButtonGroup
            value={selectedMetric}
            exclusive
            onChange={(e, newMetric) => newMetric && setSelectedMetric(newMetric)}
            size="small"
          >
            <ToggleButton value="overview">Overview</ToggleButton>
            <ToggleButton value="optimizations">Optimizations</ToggleButton>
            <ToggleButton value="insights">Insights</ToggleButton>
          </ToggleButtonGroup>
        </CardContent>
      </Card>

      {/* Main Content */}
      {selectedMetric === 'overview' && renderOverview()}
      {selectedMetric === 'optimizations' && renderOptimizations()}
      {selectedMetric === 'insights' && renderInsights()}
    </Box>
  );
}
